import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageSquare, Check } from "lucide-react";
import SEO from "../components/SEO";
import { PHONE, PHONE_TEL } from "../data/content";

const rates = [
  { hour: "First hour", price: "$139", note: "Includes up to the first 60 minutes of on-site service. A flat, honest rate — no travel fee, no callout fee." },
  { hour: "Second hour", price: "$99", note: "If more time is needed, the second hour is charged at a reduced rate. The technician will discuss this with you before continuing." },
  { hour: "Third hour", price: "$79", note: "Longer jobs get cheaper as they go — the third hour and beyond is our lowest rate." },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "How much does on-site IT support cost in Adelaide?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Tech Emergency charges $139 for the first hour of on-site service, $99 for the second hour and $79 for the third hour. No callout fee, no travel fee. EFTPOS or cash payment upon job completion.",
      },
    },
    {
      "@type": "Question",
      name: "How do I pay?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Easy Payment Onsite — EFTPOS or cash payment upon job completion. No online payments, no cards on file.",
      },
    },
  ],
};

export default function Pricing() {
  return (
    <>
      <SEO
        title="Pricing — On-Site IT Support Rates Adelaide | Tech Emergency"
        description="Simple, upfront pricing: $139 first hour, $99 second hour, $79 third hour. No callout fee, no travel fee. EFTPOS or cash payment upon job completion. Adelaide-wide on-site IT support."
        path="/pricing"
        jsonLd={jsonLd}
      />

      <section className="pt-10 md:pt-16 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-3">/ Clear · Upfront · No surprises</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-none mb-4" data-testid="pricing-heading">
            Simple, upfront<br />pricing.
          </h1>
          <p className="text-zinc-400 max-w-2xl mb-6">
            Know what you'll pay before you enquire. Our standard on-site service pricing is
            simple and clearly explained before we make an appointment.
          </p>
          <div
            className="inline-flex items-center gap-2.5 px-4 py-3 border border-white/15 text-sm sm:text-base font-semibold text-white"
            style={{ background: "rgba(255,59,48,0.08)" }}
            data-testid="pricing-eftpos-note"
          >
            <img src="/images/tap-and-go.png" alt="Tap and go contactless payment" className="h-7 w-auto" />
            Easy Payment Onsite — EFTPOS or cash payment upon job completion.
          </div>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-4" data-testid="pricing-rate-cards">
            {rates.map((r, i) => (
              <div
                key={r.hour}
                className="te-card p-7 flex flex-col"
                style={i === 0 ? { borderColor: "var(--te-primary)" } : undefined}
                data-testid={`pricing-card-${i + 1}`}
              >
                <div className="te-overline mb-2">{r.hour}</div>
                <div className="text-5xl font-extrabold tracking-tighter mb-3" style={{ color: i === 0 ? "var(--te-primary)" : "#fff" }}>
                  {r.price}
                </div>
                <p className="text-sm text-zinc-400 leading-relaxed">{r.note}</p>
              </div>
            ))}
          </div>
          <p className="text-zinc-300 mt-6 max-w-3xl">
            <strong className="text-white">No hidden hourly rates.</strong> You don't need to
            contact us just to find out our standard service pricing — it's right here on this
            page. If more time is required, the technician will discuss it with you{" "}
            <strong className="text-white">before continuing</strong>.
          </p>
        </div>
      </section>

      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-4">
          <div className="te-card p-7" data-testid="pricing-hardware-section">
            <h2 className="text-lg md:text-xl font-bold tracking-tight mb-3">Hardware &amp; software</h2>
            <p className="text-sm text-zinc-400 leading-relaxed mb-3">
              The prices above cover the technician's <strong className="text-white">service and labour</strong>.
              If any hardware, parts, software or licences are required to complete the work, the
              technician will <strong className="text-white">discuss this with you during the visit</strong>,
              including any additional costs, before anything is purchased or supplied.
            </p>
            <p className="text-sm text-zinc-400 leading-relaxed">
              No additional hardware or software costs will ever be added without discussing them
              with you first.
            </p>
          </div>

          <div className="te-card p-7" data-testid="pricing-payment-section">
            <h2 className="text-lg md:text-xl font-bold tracking-tight mb-3">Payment on completion</h2>
            <ul className="space-y-2.5 text-sm text-zinc-400">
              {[
                "Payment is due by EFTPOS or cash on completion of the work.",
                "EFTPOS is contactless — tap your card or phone to pay.",
                "No online payments, no cards on file, no surprise charges.",
                "A receipt will be provided following payment.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5">
                  <Check size={15} className="mt-0.5 flex-shrink-0" style={{ color: "var(--te-primary)" }} />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-card p-8 md:p-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3">Ready to get it fixed?</h2>
            <p className="text-zinc-400 mb-6">
              Free over-the-phone triage first — you'll talk to a real local technician, not a call centre.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href={PHONE_TEL} data-testid="pricing-call-btn" className="te-btn te-btn-primary">
                <Phone size={16} /> Call {PHONE}
              </a>
              <Link to="/book" data-testid="pricing-enquiry-btn" className="te-btn te-btn-ghost">
                <MessageSquare size={16} /> Send an enquiry
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
